import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Clock, EnvelopeSimple, Package, Receipt, SignIn } from "@phosphor-icons/react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import FarmersNav from "@/components/FarmersNav";
import { useAuth } from "@/contexts/AuthContext";

/** FarmersOrders — the farmer's order inbox: every paid checkout from their
 *  farm site lands here, ready to fulfil. */
export default function FarmersOrders() {
  const { user, openLogin } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    http.get("/farmers/shop/orders/mine").then(({ data }) => setData(data)).catch(() => setData({ orders: [], counts: {} }));
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const fulfil = async (orderId) => {
    if (busy) return;
    setBusy(orderId);
    try {
      await http.post(`/farmers/shop/orders/${orderId}/fulfil`);
      toast.success("Marked as fulfilled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the order");
    } finally {
      setBusy(null);
    }
  };

  const counts = data?.counts || {};

  return (
    <main className="min-h-screen bg-[#FBF9F4] text-[#14532D] pt-28 md:pt-32 pb-24" data-testid="farmers-orders-page">
      <div className="max-w-[1000px] mx-auto px-5 md:px-8">
        <FarmersNav />
        <div className="mt-6 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3F6212]">Farmers Pulse · Shop</div>
        <h1 className="mt-1 font-serif-display text-4xl md:text-5xl" data-testid="orders-title">Order inbox</h1>
        <p className="mt-2 max-w-2xl text-base text-[#4B5563]">
          Every paid order from your farm site lands here. Pack it, hand it over, mark it fulfilled.
        </p>

        {!user ? (
          <div className="mt-10 rounded-3xl border border-black/5 bg-white p-8 text-center" data-testid="orders-signin">
            <SignIn size={36} weight="light" className="mx-auto text-[#166534]" />
            <h2 className="mt-3 font-serif-display text-2xl">Sign in to see your orders</h2>
            <p className="mt-1 text-sm text-[#4B5563]">Your order inbox is private to your farm account.</p>
            <button onClick={openLogin} data-testid="orders-signin-btn"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D]">
              Sign in
            </button>
          </div>
        ) : !data ? (
          <div className="mt-10 text-center text-sm text-[#6B7280]">Loading orders…</div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-3 gap-3" data-testid="orders-counts">
              {[
                ["To fulfil", counts.paid || 0, "text-[#B45309]"],
                ["Fulfilled", counts.fulfilled || 0, "text-[#166534]"],
                ["Awaiting payment", counts.pending || 0, "text-[#6B7280]"],
              ].map(([label, n, cls]) => (
                <div key={label} className="rounded-2xl border border-black/5 bg-white p-4 text-center">
                  <div className={`font-serif-display text-3xl ${cls}`} data-testid={`orders-count-${label.toLowerCase().replace(/\s+/g, "-")}`}>{n}</div>
                  <div className="mt-1 text-xs text-[#6B7280]">{label}</div>
                </div>
              ))}
            </div>

            {data.orders.length === 0 ? (
              <div className="mt-8 rounded-3xl border border-dashed border-[#166534]/25 bg-white p-8 text-center" data-testid="orders-empty">
                <Receipt size={36} weight="light" className="mx-auto text-[#166534]" />
                <h2 className="mt-3 font-serif-display text-2xl">No orders yet</h2>
                <p className="mt-1 text-sm text-[#4B5563]">Share your farm site — when a shopper buys, the order appears here the moment they pay.</p>
                <Link to="/farmers/websites" className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#166534]/30 px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#166534] hover:bg-[#F0FDF4]">
                  Open my farm site
                </Link>
              </div>
            ) : (
              <div className="mt-8 space-y-3" data-testid="orders-list">
                {data.orders.map((o) => (
                  <div key={o.id} className="rounded-2xl border border-black/5 bg-white p-5 flex flex-wrap items-center gap-4"
                    data-testid={`order-row-${o.id}`}>
                    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                      o.status === "fulfilled" ? "bg-[#F0FDF4] text-[#166534]" :
                      o.payment_status === "paid" ? "bg-[#FFFBEB] text-[#B45309]" : "bg-black/[0.04] text-black/40"
                    }`}>
                      {o.status === "fulfilled" ? <CheckCircle size={20} weight="fill" /> :
                       o.payment_status === "paid" ? <Package size={20} weight="bold" /> : <Clock size={20} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[#1F2937]" data-testid={`order-name-${o.id}`}>
                        {o.product_name} × {o.quantity}{o.unit ? ` ${o.unit}${o.quantity > 1 ? "s" : ""}` : ""}
                      </div>
                      <div className="mt-0.5 text-xs text-[#6B7280]">
                        {(o.paid_at || o.created_at || "").slice(0, 10)} · {o.amount?.toFixed(2)} {o.currency}
                        {o.buyer_email && (
                          <span className="inline-flex items-center gap-1 ml-2">
                            <EnvelopeSimple size={11} /> {o.buyer_email}
                          </span>
                        )}
                      </div>
                    </div>
                    {o.status === "fulfilled" ? (
                      <span className="rounded-full bg-[#F0FDF4] border border-[#166534]/20 px-3.5 py-1.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#166534]"
                        data-testid={`order-status-${o.id}`}>Fulfilled</span>
                    ) : o.payment_status === "paid" ? (
                      <button onClick={() => fulfil(o.id)} disabled={busy === o.id} data-testid={`order-fulfil-${o.id}`}
                        className="rounded-full bg-[#166534] px-4 py-2 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-white hover:bg-[#14532D] disabled:opacity-60">
                        {busy === o.id ? "Updating…" : "Mark fulfilled"}
                      </button>
                    ) : (
                      <span className="rounded-full bg-black/[0.04] px-3.5 py-1.5 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-black/40"
                        data-testid={`order-status-${o.id}`}>Awaiting payment</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
